import React from 'react';
import { find, get, isEmpty } from 'lodash';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

// React view component to render
import { authFlowCallerIdentificationThirdParty as AuthFlowCallerIdentificationThirdPartyView } from 'ui/views/widgets/authFlowCallerIdentificationThirdParty';
import { AuthFlowCallerIdentificationThirdPartyService } from 'services/authFlowCallerIdentificationThirdParty';
import { SERVICE_RESPONSE_STATUS, CRUD_OPERATION } from 'constants/common';
import { REDUCER } from 'constants/entity-names';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import { updateCallState, initCallState } from 'store/actions/callState';
import { isInbound, getCallerTypeFromName } from 'resolvers/callStateEntities';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { AuthFlowCallerIdentificationThirdParty } from 'ui/containers/AuthFlowCallerIdentificationThirdParty';
 *
 * // render
 * <AuthFlowCallerIdentificationThirdParty propName='propValue'/>
 */
export class _AuthFlowCallerIdentificationThirdParty extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind methods to instance
		this.__bind();

		// component state
		this.state = {
			isFetching: true,
			networkStatus: undefined,
			componentData: undefined,
			mode: 'editable', // could be `readonly` or `editable`
            thirdPartyInfo: {},
            isOrgDetailVisible: false
		};

		// create service instance
		this.authFlowCallerIdentificationThirdPartyService = new AuthFlowCallerIdentificationThirdPartyService();

		// start data fetch
		this.fetchComponentData();
    }

    // bind methods and events to instance
	__bind() {
		this.handleToggleEditMode = this.handleToggleEditMode.bind( this );
        this.handleFormSubmit = this.handleFormSubmit.bind( this );
        this.handleActionClick = this.handleActionClick.bind( this );
    }
    
    /**
	 * @desc Fetch component response.
	 */
	async fetchComponentData() {
		log.debug( 'authFlowCallerIdentificationThirdParty.fetchComponentData()' );

		// cancel ongoing request
		this.authFlowCallerIdentificationThirdPartyService.cancel();

		// fetch component data
		const {
			status,
			data,
		} = await this.authFlowCallerIdentificationThirdPartyService.fetchComponentData(
			{
				url: this.props.widget.url,
			}
		);

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
			} );
		} else {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				componentData: data,
			} );
		}
    }
    
    /**
	 * @desc Toggle between read and edit mode.
	 */
	handleToggleEditMode( editable = true ) {
		log.debug( 'authFlowCallerIdentificationThirdParty.handleToggleEditMode() ' );

		this.setState( {
			mode: editable ? 'editable' : 'readonly',
		} );
    }
    
    /**
	 * @desc Handle form submit.
	 */
	async handleFormSubmit( { title, firstName, lastName } = {} ) {
		log.debug( 'authFlowCallerIdentificationThirdParty.handleFormSubmit()' );

		// get save item action
		const saveAction = find(
			this.state.componentData.sections.callerDetails.actions,
			{ name: 'save' }
		);

		// cancel ongoing request
		this.authFlowCallerIdentificationThirdPartyService.cancel();

		// show page level preloader
		pageLevelPreloader.show();

		// get status and service data
		const {
			status,
			data
		} = await this.authFlowCallerIdentificationThirdPartyService.addUpdateItem( {
			url: saveAction.url,
			data: {
				operation: isEmpty( this.state.thirdPartyInfo )
					? CRUD_OPERATION.CREATE
					: CRUD_OPERATION.UPDATE,
				title,
				firstName,
				lastName
			},
		} );

		// hide page level preloader
		pageLevelPreloader.hide();

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				mode: 'readonly',
                thirdPartyInfo: data,
                isOrgDetailVisible: true
			} );
		} else {
			errorOverlay.open(); // show error
		}
    }

    /**
     * @desc Action handler for next button click.
     */
    handleActionClick( { item = {} } = {} ) {
        log.debug( 'authFlowCallerIdentificationThirdParty.handleActionClick()'  );

        // get member detail
        const firstName = get( item, 'firstName', '' );
        const lastName = get( item, 'lastName', '' );
        const orgName = get( item, 'organizationName', '' );
        const callbackNumber = get( item, 'callbackNumber', '' );
        const taxId = get( item, 'taxId', '' );

        // extract actions
        const actions = get( this.state.componentData, 'actions' );

        // getting caller type value
        const callerType = getCallerTypeFromName( this.props.widget.value.callerType );

        /*---------*/

        // if its inbound call, register an inbound caller
        if( isInbound() ) {
            const action = find( actions, { name: 'nextInbound'} );

            return this.registerInboundCaller( {
                actionName: action.name,
                url: action.url,
                payload: {
                    firstName,
                    lastName,
                    orgName,
                    callbackNumber,
                    taxId
                },
                callerType: callerType
            } );
        }
    }

    /**
     * @desc Request backend to register `member` as the caller for the ongoing inbound call.
     */
    async registerInboundCaller( { actionName, url, payload, callerType } ) {
        log.debug( 'authFlowCallerIdentificationThirdParty.registerInboundCaller()' );

        // cancel ongoing request
        this.authFlowCallerIdentificationThirdPartyService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // fetch component data
        const { status } = await this.authFlowCallerIdentificationThirdPartyService.registerInboundCaller( {
            url: url,
            data: payload,
            callerType: callerType
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
            }, () => {

                // update call data in the store
                this.props.updateCallState( {
                    callerId: payload.id,
                    callerType: callerType,
                    callerInfo: {
                        id: null,
                        refId: null,
                        type: callerType,
                        firstName: null,
                        lastName: null,
                    },
                } );
                
                // move to the next step
                this.props.onNextActionClick( { name: actionName } );
            } );
        } else {
            errorOverlay.open(); // show error
        }
    }
    
    /*********************************************/

	/**
	 * @desc When component unMounts, cleanup resources.
	 */
	componentWillUnmount() {
		this.authFlowCallerIdentificationThirdPartyService.cancel(); // cancel ongoing requests
    }
    
    /*********************************************/

    /**
     * @desc Render a React view component
     */
    render() {

        // getting caller type value
		const callerType = getCallerTypeFromName(
			this.props.widget.value.callerType
        );
        
        return (
            <AuthFlowCallerIdentificationThirdPartyView
                className={ this.props.className }
				isFetching={ this.state.isFetching }
				networkStatus={ this.state.networkStatus }
				hasCallStarted={ !isEmpty( this.props.callState ) }
				componentData={ get( this.state, 'componentData', {} ) }
				onFormSubmit={ this.handleFormSubmit }
				onToggleEditMode={ this.handleToggleEditMode }
				mode={ this.state.mode }
				callerType={ callerType }
                thirdPartyInfo={ this.state.thirdPartyInfo }
                isOrgDetailVisible={ this.state.isOrgDetailVisible }
                handleActionClick={ this.handleActionClick }
            />
        );
    }
}

// set display name
_AuthFlowCallerIdentificationThirdParty.displayName = 'AuthFlowCallerIdentificationThirdParty';

// set default props
_AuthFlowCallerIdentificationThirdParty.defaultProps = {};

// prop types
_AuthFlowCallerIdentificationThirdParty.propTypes = {};

/************************************************************ */

// map state to props
const mapStateToProps = ( state ) => {
    return {
        callState: state[ REDUCER.CALL_STATE ],
    };
};

// map dispatch to props
const mapDispatchToProps = ( dispatch ) => {
    return {
        initCallState: bindActionCreators( initCallState, dispatch ),
        updateCallState: bindActionCreators( updateCallState, dispatch ),
    };
};

// return component with access to Redux store
export const AuthFlowCallerIdentificationThirdParty = withRouter( connect( mapStateToProps, mapDispatchToProps )( _AuthFlowCallerIdentificationThirdParty ) );
